import { useEffect, useState } from "react";
import {
  AlignItems,
  Box,
  Button,
  ButtonVariant,
  ColorPreset,
  FontWeight,
  Form,
  H2,
  JustifyContent,
  P,
  ReadingOptimized,
  Separator,
  Space,
  TypePreset,
  PlainLink,
  HoverEffect,
  TextAlign,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { ChangeHandler, FormProvider, useForm } from "react-hook-form";
import { CreditorType } from "@gocardless/api/dashboard/types";

import {
  COOKIES_LINK,
  REGULATORY_INFO,
  SignUpFormData,
  SignUpFormField,
} from "../../consts";
import { useSignUp } from "../../useSignUp";
import {
  FirstNameField,
  LastNameField,
} from "../../form-field-components/FirstLastNameField";
import { OrganisationField } from "../../form-field-components/OrganisationField";
import { GeoDropdown } from "../../form-field-components/GeoDropdown";
import { CreditorTypeDropdown } from "../../form-field-components/CreditorTypeDropdown";
import { EmailField } from "../../form-field-components/EmailField";
import { FinancialMetricFields } from "../../form-field-components/FinancialMetricFields";
import { PasswordComponent } from "../../form-field-components/PasswordComponent";
import { LanguageDropdown } from "../../form-field-components/LanguageDropdown";
import { TermsAndConditionsCheck } from "../../form-field-components/TermsAndConditionsCheck";
import { SignUpAccountType } from "../../SignUp";

import {
  ReferralEmailSource,
  ReferralEmailVariation,
} from "src/common/trackingEventAttributes";
import { enumFromStringValue } from "src/utils/types";
import { useSupportedCountries } from "src/libraries/creditor/hooks";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { isSandboxEnvironment } from "src/common/consts";
import { SandboxTag } from "src/common/SandboxTag";
import PhoneNumberInput from "src/components/ui/phone-number-input/PhoneNumberInput";
import {
  ReCAPTCHA,
  submitReCAPTCHA,
} from "src/technical-integrations/reCAPTCHA";
import { CountryCodeSupported } from "src/common/country";
import {
  followsPasswordUserInfoPolicy,
  invalidGoCardlessEmailSignUp,
  passwordLengthValidation,
  VALID_EMAIL_REGEX,
} from "src/utils/inputValidation";
import {
  UserLocale,
  ShorthandLocale,
  useI18n,
} from "src/technical-integrations/i18n";
import { getRouteURL, Route } from "src/common/routing";

export const SIGNUP_FORM_STORAGE_KEY = "gc.signup_form_details";

export interface SignUpFormComponentProps {
  emailAddress: string;
  userNavigatorLocale: CountryCodeSupported;
  accountType: SignUpAccountType;
  referralGeo: string | null;
  referralCode: string | null;
  country: CountryCodeSupported;
  isPaymentProvider?: boolean;
  hasFsbMemberDiscount?: boolean;
  referralSource?: ReferralEmailSource | null;
  referralVariation?: ReferralEmailVariation | null;
  referralCodeName?: string | null;
}

export const SignUpFormComponent = ({
  emailAddress,
  userNavigatorLocale,
  accountType,
  referralGeo,
  referralCode,
  referralSource,
  referralVariation,
  referralCodeName,
  isPaymentProvider,
  hasFsbMemberDiscount,
}: SignUpFormComponentProps) => {
  const [passwordValid, setPasswordValid] = useState(false);
  const [focusTime, setFocusTime] = useState<number>(0);
  const [minPassword, setMinPassword] = useState(
    passwordLengthValidation(emailAddress)
  );
  const [userLocale, updateLocale] = useI18n();
  const { apiFieldErrors, submitSignUpForm, isSubmitting } = useSignUp(
    referralGeo,
    referralCode,
    referralSource,
    referralVariation,
    referralCodeName
  );
  const { isCreditorTypeSupported } = useSupportedCountries();
  const { sendEvent } = useSegment();

  const formData = sessionStorage.getItem(SIGNUP_FORM_STORAGE_KEY);
  useEffect(() => {
    if (formData) {
      sessionStorage.removeItem(SIGNUP_FORM_STORAGE_KEY);
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSalesServed = accountType === SignUpAccountType.SALES_SERVED;

  const SignUpFormHeadingText = (() => {
    if (isSandboxEnvironment)
      return (
        <Trans id="sign-up.form.heading-sandbox">
          Finish signing up for a free sandbox testing account
        </Trans>
      );

    if (isSalesServed || hasFsbMemberDiscount)
      return (
        <Trans id="sign-up.form.heading-sales-served">Finish signing up</Trans>
      );

    return <Trans id="sign-up.form.heading">Finish signing up for free</Trans>;
  })();

  const ExistingAccountText = (() => {
    if (isSandboxEnvironment)
      return (
        <Trans id="Already have an account sandbox">
          Already have a sandbox account?
        </Trans>
      );

    return <Trans id="Already have an account">Already have an account?</Trans>;
  })();

  const methods = useForm<SignUpFormData>({
    defaultValues: formData
      ? JSON.parse(formData)
      : {
          given_name: "",
          family_name: "",
          email: emailAddress,
          phone_number: "",
          organisation_name: "",
          geo: userNavigatorLocale,
          creditor_type: undefined,
          password: "",
          recaptcha_token: "",
          language: ShorthandLocale[userLocale],
          terms_and_conditions: false,
        },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const {
    clearErrors: clearError,
    register,
    formState,
    watch,
    getValues,
    setError,
    trigger: triggerValidation,
    control,
  } = methods;
  const { isDirty, errors, isValid } = formState;

  const formFieldsToWatch = watch([
    "given_name",
    "family_name",
    "geo",
    "email",
    "language",
    "creditor_type",
    "password",
  ]);

  const watchFormFields = {
    given_name: formFieldsToWatch[0],
    family_name: formFieldsToWatch[1],
    geo: formFieldsToWatch[2],
    email: formFieldsToWatch[3],
    language: formFieldsToWatch[4],
    creditor_type: formFieldsToWatch[5],
    password: formFieldsToWatch[6],
  };

  const emailValueUpdated: ChangeHandler = async (event) => {
    const emailValue = event.target.value;
    if (
      watchFormFields.password !== "" &&
      watchFormFields.password.length < passwordLengthValidation(emailValue)
    ) {
      setPasswordValid(false);
      setError("password", { type: "minLength" });
    } else {
      setPasswordValid(true);
      clearError("password");
    }
    setMinPassword(passwordLengthValidation(emailValue));
  };

  const passwordUpdated: ChangeHandler = async (event) => {
    const passwordValue = event.target.value;
    const isValidPasswordLength =
      passwordValue !== "" && passwordValue.length >= minPassword;

    if (!isValidPasswordLength) {
      setPasswordValid(false);
      setError("password", { type: "minLength" });
      return;
    }

    const isValidPassword = followsPasswordUserInfoPolicy(
      passwordValue.toLowerCase(),
      watchFormFields.family_name || "",
      watchFormFields.given_name || "",
      watchFormFields.email || ""
    );

    if (!isValidPassword) {
      setPasswordValid(false);
      setError("password", { type: "passwordUserInfoPolicy" });
      return;
    }

    setPasswordValid(true);
    clearError("password");
  };

  const submitButtonDisabled =
    !isDirty ||
    !isValid ||
    isSubmitting ||
    (!isPaymentProvider &&
      !isCreditorTypeSupported(
        enumFromStringValue(CountryCodeSupported, watchFormFields.geo),
        enumFromStringValue(CreditorType, watchFormFields.creditor_type)
      ));

  const changeLocale = (newLocale: string) => {
    const localeValue = newLocale + "-" + newLocale.toUpperCase();
    const formValues = getValues();
    formValues.terms_and_conditions = false;

    sessionStorage.setItem(SIGNUP_FORM_STORAGE_KEY, JSON.stringify(formValues));
    updateLocale(localeValue as UserLocale);
  };

  const tokenAvailable = (token: string) => {
    const data = {
      ...getValues(),
      recaptcha_token: token,
      account_type: accountType,
      is_payment_provider: isPaymentProvider,
    };

    if (referralCode) {
      data.referral_code = referralCode;
    }

    if (isPaymentProvider) {
      data.creditor_type = CreditorType.Company;
    }

    if (hasFsbMemberDiscount) {
      data.has_fsb_member_discount = hasFsbMemberDiscount;
    }

    submitSignUpForm(data);
  };

  // referral_code is ignored if in sandbox
  const hasReferralCode = !!(!isSandboxEnvironment && referralCode);

  useEffect(() => {
    sendEvent(TrackingEvent.SIGNUP_COMPANY_INFO_PAGE_VIEWED, {
      has_referral_code: hasReferralCode,
      partner_name: null,
      business_location: userNavigatorLocale,
      ...(hasReferralCode && {
        referral_source: referralSource,
        ...(referralVariation && { referral_variation: referralVariation }),
        ...(referralCodeName && { referrer: referralCodeName }),
        referralGeo: referralGeo,
      }),
    });
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidCreditorType = () =>
    watchFormFields.geo &&
    watchFormFields.creditor_type &&
    !isCreditorTypeSupported(
      enumFromStringValue(CountryCodeSupported, watchFormFields.geo),
      enumFromStringValue(CreditorType, watchFormFields.creditor_type)
    );

  return (
    <FormProvider {...methods}>
      <Box
        layout="flex"
        flexDirection="column"
        justifyContent={JustifyContent.Center}
        height="100%"
      >
        <ReadingOptimized alignText={AlignItems.FlexStart} textWidth="100%">
          <SandboxTag spaceBelow={1} />
          <H2 spaceBelow={2} size={[5, null, null, 6]}>
            {SignUpFormHeadingText}
          </H2>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              submitReCAPTCHA();
            }}
          >
            <Box layout="flex" flexDirection={["column", null, "row"]}>
              <Box
                width={["100%", null, "50%"]}
                spaceBelow={2}
                spaceAfter={[null, null, 1]}
              >
                <FirstNameField
                  {...register(`given_name`, {
                    required: true,
                    deps: ["password"],
                  })}
                  error={errors.given_name}
                  apiError={apiFieldErrors.get(SignUpFormField.given_name)}
                />
              </Box>
              <Box width={["100%", null, "50%"]} spaceBelow={2}>
                <LastNameField
                  {...register(`family_name`, {
                    required: true,
                    deps: ["password"],
                  })}
                  error={errors.family_name}
                  apiError={apiFieldErrors.get(SignUpFormField.family_name)}
                />
              </Box>
            </Box>
            <OrganisationField
              {...register(`organisation_name`, {
                required: true,
                onBlur: (e) => {
                  sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
                    label: "organisation_name",
                    type: "Blur",
                    value: e.currentTarget.value,
                  });
                },
              })}
              error={errors.organisation_name}
              apiError={apiFieldErrors.get(SignUpFormField.organisation_name)}
            />
            <Space v={2} />
            <GeoDropdown
              {...register(`geo`, { required: true })}
              apiError={apiFieldErrors.get(SignUpFormField.geo)}
              userNavigatorLocale={userNavigatorLocale}
              referralCode={referralCode}
              hasFsbMemberDiscount={hasFsbMemberDiscount}
            />
            <Space v={2} />
            {!isPaymentProvider && (
              <CreditorTypeDropdown
                {...register(`creditor_type`, {
                  required: true,
                  onBlur: () => {
                    sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
                      label: "creditor_type",
                      type: "Blur",
                    });
                  },
                  onChange: (event) => {
                    sendEvent(
                      TrackingEvent.SIGNUP_BUSINESS_TYPE_FIELD_UPDATED,
                      {
                        label: event.target.value,
                        ...(invalidCreditorType() && {
                          error_type: "Unsupported business type",
                          business_location: SignUpFormField.geo,
                        }),
                      }
                    );
                  },
                })}
                geoValue={watchFormFields.geo}
                error={errors.creditor_type}
                apiError={apiFieldErrors.get(SignUpFormField.creditor_type)}
              />
            )}
            <Space v={2} />
            <EmailField
              emailAddress={emailAddress}
              {...register(`email`, {
                required: true,
                pattern: VALID_EMAIL_REGEX,
                onChange: emailValueUpdated,
                deps: ["password"],
                onBlur: () => {
                  const duration: number = focusTime
                    ? new Date().getTime() - focusTime
                    : 0;
                  sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
                    label: "email",
                    type: "Blur",
                    time_spent: (duration / 1000).toFixed(2),
                  });
                },
                validate: (value) => !invalidGoCardlessEmailSignUp(value),
              })}
              setFocusTime={setFocusTime}
              error={errors.email}
              apiError={apiFieldErrors.get(SignUpFormField.email)}
              autoComplete={false}
            />
            <Space v={2} />
            <PhoneNumberInput
              name="phone_number"
              defaultCountryGeo={watchFormFields.geo}
              control={control}
              error={errors.phone_number}
              apiError={apiFieldErrors.get(SignUpFormField.phone_number)}
              label={<Trans id="Work phone">Work phone</Trans>}
              supportedGeosOnly={false}
              required
            />
            <Space v={2} />
            <PasswordComponent
              {...register(`password`, {
                required: true,
                minLength: passwordLengthValidation(watchFormFields.email),
                deps: ["email", "given_name", "family_name"],
                onChange: passwordUpdated,
              })}
              passwordValid={passwordValid}
              minPassword={minPassword}
              error={errors.password}
              apiError={apiFieldErrors.get(SignUpFormField.password)}
            />
            <Space v={2} />
            {!isPaymentProvider && (
              <>
                <FinancialMetricFields
                  geoValue={watchFormFields.geo}
                  error={errors.creditor_type}
                  apiError={apiFieldErrors.get(SignUpFormField.creditor_type)}
                />
                <Space v={2} />
              </>
            )}
            <ReCAPTCHA tokenAvailable={tokenAvailable} />
            <TermsAndConditionsCheck
              {...register(`terms_and_conditions`, {
                required: true,
                onChange: () => {
                  triggerValidation();
                },
              })}
              error={errors.terms_and_conditions}
            />
            <Space v={3} />
            <Button
              type="submit"
              variant={ButtonVariant.PrimaryOnLight}
              disabled={submitButtonDisabled}
            >
              <Trans id="Get started">Get started</Trans>
            </Button>
            <Space v={1.5} />
            <P color={ColorPreset.TextOnLight_01} size={3}>
              {ExistingAccountText}{" "}
              <PlainLink
                href={getRouteURL({
                  route: Route.SignIn,
                })}
                onClick={() => sendEvent(TrackingEvent.SIGNUP_LOGIN_CLICKED)}
                effect={HoverEffect.TextDecoration}
                textDecoration="underline"
                weight={FontWeight.SemiBold}
                textAlign={TextAlign.Center}
              >
                <Trans id="Sign in">Sign in</Trans>
              </PlainLink>
            </P>
          </Form>
          <Box spaceAbove={4}>
            <Separator
              direction="block-horizontal"
              color={ColorPreset.BorderOnLight_04}
            />
            <Space v={2} />
            <P
              preset={TypePreset.Body_01}
              size={2}
              color={ColorPreset.TextOnLight_02}
            >
              <Trans id="sign-up.form.footer-text-1">
                We may email or call you from time to time to tell you about our
                services. You can always opt out by clicking ‘unsubscribe’ or
                informing the caller.
              </Trans>{" "}
              <PlainLink
                href={COOKIES_LINK}
                effect={HoverEffect.TextDecoration}
                textDecoration="underline"
                textAlign={TextAlign.Center}
                target="_blank"
              >
                <Trans id="Cookies">Cookies</Trans>
              </PlainLink>{" "}
              <Trans id="sign-up.form.footer-text-2">
                help us operate and improve our services.
              </Trans>
            </P>
            <P
              preset={TypePreset.Body_01}
              spaceAbove={2}
              weight={FontWeight.SemiBold}
              size={2}
              color={ColorPreset.TextOnLight_02}
            >
              <Trans id="FCA-text">
                GoCardless Ltd is authorised by the Financial Conduct Authority
                under the Payment Services Regulations 2017 (Ref 597190). For
                GoCardless SAS and Inc. click{" "}
                <PlainLink
                  layout="inline"
                  textDecoration="underline"
                  href={REGULATORY_INFO}
                  target="_blank"
                  color={ColorPreset.BackgroundDark_01}
                >
                  here
                </PlainLink>
                .
              </Trans>
            </P>
            <Box spaceAbove={2} spaceBelow={2} width="max-content">
              <LanguageDropdown
                {...register(`language`, {
                  required: true,
                })}
                defaultLanguage={watchFormFields.language}
                changeLocale={changeLocale}
                apiError={apiFieldErrors.get(SignUpFormField.language)}
              />
            </Box>
          </Box>
        </ReadingOptimized>
      </Box>
    </FormProvider>
  );
};
